import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { combineLatest, filter, forkJoin, take } from 'rxjs';
import { AddCicdExecutionModalComponent } from '../../../components/modal-templates/add-cicd-execution-modal/add-cicd-execution-modal.component';
import { CicdExecutionStep2Component, CicdStep2Result } from '../../../components/common/cicd-execution-step2/cicd-execution-step2.component';
import { CicdConfigInfoModalComponent } from '../../../components/modal-templates/cicd-config-info-modal/cicd-config-info-modal.component';
import { DeleteConfirmationComponent } from '../../../components/modal-templates/delete-confirmation/delete-confirmation.component';
import { ZorroTableComponent } from '../../../components/common/custom-table/zorro-table.component';
import { cicdExecutionsColumns } from '../../../core/table-templates/cicd-executions-table';
import { ProjectService } from '../../../core/services/project/project.service';
import { RestService } from '../../../core/services/rest.service';
import { ToasterService } from '../../../core/services/toaster-service';
import { MessageEnum } from '../../../core/utils/message.enum';

export interface CICDExecution {
  id: string;
  name: string;
  testFailureAnalysis: boolean;
  selfHealing: boolean;
  releaseId: string | null;
  automationProjectId: string;
  automationExecutionPlanRequestDTOS: any[];
}

@Component({
  selector: 'app-cicd-executions',
  standalone: true,
  imports: [CommonModule, ZorroTableComponent, CicdConfigInfoModalComponent],
  templateUrl: './cicd-executions.component.html',
  styleUrl: './cicd-executions.component.scss',
})
export class CICDExecutionsComponent implements OnInit {
  @ViewChild(ZorroTableComponent) zorroTable!: ZorroTableComponent;

  private readonly modalService = inject(NgbModal);
  private readonly restService = inject(RestService);
  private readonly projectService = inject(ProjectService);
  private readonly toaster = inject(ToasterService);

  private projectId: string = '';
  private automationProjectId: string = '';

  columns = cicdExecutionsColumns;
  executions: CICDExecution[] = [];
  isLoading: boolean = false;
  isRemoving: boolean = false;

  private _selectedRows: any[] = [];

  get hasSelection(): boolean {
    return this._selectedRows.length > 0;
  }

  totalExecutions = 0;
  pageStart = 1;
  pageEnd = 10;
  selectedCount = 0;
  allSelected = false;

  tableOptions = {
    pageSize: 10,
    showPagination: true,
    showPageSizeChanger: false,
  };

  get rangeLabel(): string {
    const safeEnd = Math.min(this.pageEnd, this.totalExecutions);
    return `${this.pageStart} - ${safeEnd} of ${this.totalExecutions}`;
  }

  ngOnInit(): void {
    combineLatest([
      this.projectService.getProjectId(),
      this.projectService.getAutomationProjectId(),
    ]).pipe(
      filter(([pid, aid]) => !!pid && !!aid),
      take(1),
    ).subscribe(([projectId, automationProjectId]) => {
      this.projectId = projectId!;
      this.automationProjectId = automationProjectId!;
      this.loadExecutions();
    });
  }

  loadExecutions(): void {
    if (!this.projectId || !this.automationProjectId) return;
    this.isLoading = true;
    this.restService.getPipelineConfigs(this.projectId, this.automationProjectId).subscribe({
      next: (res: any) => {
        this.executions = (res?.data ?? []).map((item: any) => ({
          ...item,
          // Map boolean flags to 'pass'/'fail' strings expected by the table columns
          testFailureAnalysis: item.testFailureAnalysis ? 'pass' : 'fail',
          uiSelfHealing:       item.selfHealing         ? 'pass' : 'fail',
          // release is now an object { id, name, ... }
          releaseName:  item.release?.name ?? '',
          releaseStatus: null,
        }));
        this.totalExecutions = this.executions.length;
        this.pageEnd = Math.min(this.tableOptions.pageSize, this.totalExecutions);
        this.isLoading = false;
      },
      error: () => {
        this.toaster.pop({ type: 'error', text: MessageEnum.FAILED_TO_LOAD_CICD_EXECUTIONS });
        this.isLoading = false;
      },
    });
  }

  onSelectAllChange(event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;
    this.allSelected = isChecked;
    this.selectedCount = isChecked ? this.totalExecutions : 0;
    this.zorroTable?.onSelectAll(event);
    this._selectedRows = isChecked ? [...this.executions] : [];
  }

  onRowSelectChange(event: any): void {
    // event.data contains the currently selected rows
    this._selectedRows = event?.data ?? [];
    this.selectedCount = this._selectedRows.length;
    this.allSelected = this._selectedRows.length === this.totalExecutions;
  }

  onTableAction(event: any): void {
    const { type, data } = event;
    const row = data?.row;
    const column = data?.column;
    if (type === 'link') {
      if (column?.key === 'viewDetails') {
        this.onViewDetails(row);
      } else if (column?.key === 'cicdConfigInfo') {
        this.onViewCICDConfig(row);
      }
    }
  }

  onTablePageChange(event: { page: number; size: number }): void {
    this.pageStart = (event.page - 1) * event.size + 1;
    this.pageEnd = Math.min(event.page * event.size, this.totalExecutions);
  }

  addNewCICDExecution(): void {
    this.openStep1();
  }

  private openStep1(): void {
    const modalRef = this.modalService.open(AddCicdExecutionModalComponent, {
      centered: true,
      windowClass: 'add-cicd-execution-modal',
      backdrop: 'static',
      keyboard: false,
      size: 'xl',
    });
    modalRef.result.then(
      (result: any) => {
        if (result?.step === 1) {
          this.openStep2(result.sections);
        }
      },
      () => { /* dismissed */ },
    );
  }

  private openStep2(sections: any[]): void {
    const modalRef = this.modalService.open(CicdExecutionStep2Component, {
      centered: true,
      windowClass: 'cicd-step2-modal',
      backdrop: 'static',
      keyboard: false,
    });
    const instance = modalRef.componentInstance as CicdExecutionStep2Component;
    instance.sections = sections;
    instance.projectId = this.projectId;
    instance.automationProjectId = this.automationProjectId;
    modalRef.result.then(
      (result: CicdStep2Result | { back: true; sections: any[] }) => {
        if ((result as any).back) {
          this.openStep1();
        } else {
          this.onExecutionSaved(result as CicdStep2Result);
        }
      },
      () => { /* dismissed */ },
    );
  }

  private onExecutionSaved(_result: CicdStep2Result): void {
    this.loadExecutions();
  }

  removeCICDExecution(): void {
    const selected = this._selectedRows;
    if (!selected.length) return;

    const count = selected.length;
    const modalRef = this.modalService.open(DeleteConfirmationComponent, {
      centered: true,
      backdrop: 'static',
    });
    modalRef.componentInstance.data = {
      title: `Remove ${count} pipeline${count > 1 ? 's' : ''}?`,
      description: 'This action cannot be undone. The selected CI/CD pipeline configurations will be permanently deleted.',
    };

    modalRef.result.then(
      (confirmed: boolean) => {
        if (!confirmed) return;
        this.isRemoving = true;
        const deletes = selected.map((row: any) =>
          this.restService.deletePipelineConfig(this.projectId, this.automationProjectId, row.id)
        );
        forkJoin(deletes).subscribe({
          next: () => {
            this.toaster.pop({ type: 'success', text: `${count} pipeline${count > 1 ? 's' : ''} removed successfully.` });
            this.isRemoving = false;
            this.allSelected = false;
            this.selectedCount = 0;
            this._selectedRows = [];
            this.loadExecutions();
          },
          error: () => {
            this.toaster.pop({ type: 'error', text: MessageEnum.FAILED_TO_REMOVE_PIPELINES });
            this.isRemoving = false;
            this.loadExecutions();
          },
        });
      },
      () => { /* dismissed */ },
    );
  }

  private onViewCICDConfig(row: any): void {
    const modalRef = this.modalService.open(CicdConfigInfoModalComponent, {
      centered: true,
      windowClass: 'cicd-config-info-modal',
      backdrop: 'static',
      keyboard: false,
      size: 'xl',
    });
    const instance = modalRef.componentInstance as CicdConfigInfoModalComponent;
    instance.pipelineId = row.id;
    instance.projectId = this.projectId;
    instance.automationProjectId = this.automationProjectId;
  }

  private onViewDetails(row: any): void {
    this.restService.getPipelineConfigById(this.projectId, this.automationProjectId, row.id).subscribe({
      next: (res: any) => {
        const pipeline = res?.data ?? res;
        const sections = this.reconstructSections(pipeline);
        this.openEditStep1(sections, pipeline);
      },
      error: () => {
        this.toaster.pop({ type: 'error', text: MessageEnum.FAILED_TO_LOAD_PIPELINE_DETAILS });
      },
    });
  }

  private reconstructSections(pipeline: any): any[] {
    const ADHOC_NAME = 'AdHocTestplan';
    return (pipeline.automationExecutionPlanRequestDTOS ?? []).map((plan: any) => {
      const isAdhoc = plan.testPlanName === ADHOC_NAME;
      const tableRows = (plan.executionScripts ?? []).map((script: any) => ({
        testCaseId: script.id,
        scriptId: script.id,
        testCaseName: script.name ?? script.id,
        version: script.version ?? null,
        increment: script.increment ?? 0,
        versionOptions: script.version ? [script.version] : [],
        executionEngine: script.agentType === 'CLUSTER'
          ? 'onDemand'
          : (script.agentId ?? script.deviceId ?? null),
        browser: script.browser ?? 'Chrome',
        browserOptions: ['Chrome', 'Firefox', 'Edge'],
        agentType: script.agentType,
        executionType: script.executionType,
        deviceId: script.deviceId ?? null,
        select: false,
      }));
      const sectionId = isAdhoc ? 'adhoc-test-plan' : `${plan.testPlanId}`;
      return {
        id: sectionId,
        title: plan.testPlanName,
        name: plan.testPlanName,
        testSuiteName: plan.testPlanName,
        testPlanName: plan.testPlanName,
        testPlanId: plan.testPlanId,
        testSuiteId: sectionId,
        itemCount: tableRows.length,
        testcaseCount: tableRows.length,
        expanded: true,
        tableColumns: [], // rebuilt by AddCicdExecutionModalComponent after agents/devices load
        tableRows,
      };
    });
  }

  private openEditStep1(sections: any[], pipeline: any): void {
    const modalRef = this.modalService.open(AddCicdExecutionModalComponent, {
      centered: true,
      windowClass: 'add-cicd-execution-modal',
      backdrop: 'static',
      keyboard: false,
      size: 'xl',
    });
    const instance = modalRef.componentInstance as AddCicdExecutionModalComponent;
    instance.editMode = true;
    instance.pipelineId = pipeline.id;
    instance.initialSections = sections;

    modalRef.result.then(
      (result: any) => {
        if (result?.step === 1) {
          this.openEditStep2(result.sections, pipeline);
        }
      },
      () => { /* dismissed */ },
    );
  }

  private openEditStep2(sections: any[], pipeline: any): void {
    const modalRef = this.modalService.open(CicdExecutionStep2Component, {
      centered: true,
      windowClass: 'cicd-step2-modal',
      backdrop: 'static',
      keyboard: false,
    });
    const instance = modalRef.componentInstance as CicdExecutionStep2Component;
    instance.sections = sections;
    instance.projectId = this.projectId;
    instance.automationProjectId = this.automationProjectId;
    instance.editMode = true;
    instance.pipelineId = pipeline.id;
    instance.initialLinkToRelease = !!pipeline.release?.name;
    instance.initialReleaseId = pipeline.release?.id ?? null;
    instance.initialTestFailureAnalysis = pipeline.testFailureAnalysis ?? false;
    instance.initialUiSelfHealing = pipeline.selfHealing ?? false;
    instance.initialName = pipeline.name ?? '';

    modalRef.result.then(
      (result: any) => {
        if (result?.back) {
          this.openEditStep1(result.sections, pipeline);
        } else {
          this.onExecutionSaved(result as CicdStep2Result);
        }
      },
      () => { /* dismissed */ },
    );
  }
}
